import * as Blockly from 'blockly/core'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import { behaviorsForScene } from './behaviors'

import { setAudioVolume } from './audioMute'
import { resumeSharedAudioContext } from './behaviors/audio'
import { releaseHtmlAudio, setHtmlAudioVolume } from './htmlAudioGraph'

import { KeyGuide } from './KeyGuide'
import { createRuntime } from './engine'
import { HudBounds, HudOverlay, HudSlots } from './panel/HudOverlay'
import { OutputLog } from './panel/OutputLog'
import { PreviewControlsBar } from './panel/PreviewControlsBar'
import { VehiclePromptButton } from './panel/VehiclePromptButton'
import {
  FIELD_COMMIT_RESTART_GRACE_MS,
  commitBlocklyFieldEdits,
} from './panel/blocklyFieldCommit'
import { downloadRbxlx } from './export'
import {
  AvatarController,
  VehiclePromptKind,
  createAvatarController,
} from './physics'
import { CameraMode, PreviewRendererHandle, createRenderer } from './render'
import { RunMotionTuner } from './render/avatar/RunMotionTuner'
import { EditablePreviewWorld, PreviewRuntime, SceneDescriptor } from './types'
import { SceneWorld } from './world'

export interface PreviewPanelProps {
  scene: SceneDescriptor
  /** オブジェクト名（descriptor.objects[].name）→ そのオブジェクトの Blockly workspace。 */
  workspaces: Map<string, Blockly.Workspace>
  /** マウント直後に自動でプレイ開始するか。 */
  autoRun?: boolean
  /**
   * パネル内蔵の操作（再生/停止/リセット）をビューポート内左下に表示するか。
   * デモ（単体マウント）は既定の true、PreviewModal は領域内へ独自配置するため false。
   */
  showControls?: boolean
  /**
   * 視点切替（KeyV ホットキー）を有効にするか（既定 false）。
   * 製品仕様はアバター視点のみで、開発者ツール（?devtools=true）でのみ有効化する。
   */
  cameraSwitchEnabled?: boolean
  /** 物理（アバター）の初期化が完了し、操作可能になったときに一度だけ呼ばれる。 */
  onReady?: () => void
  /** 視点モードが変わったとき（マウント時の初期値含む）に現在モードを通知する。 */
  onCameraModeChange?: (mode: CameraMode) => void
  /**
   * 実行中フラグが変わったときに通知する。パネルが running の単一情報源で、
   * 外側 UI（ヘッダーの再生/停止ボタン等）はこれを購読して活性状態を揃える。
   */
  onRunningChange?: (running: boolean) => void
  /**
   * 音量(0..1)が変わったときに通知する。パネルが volume の単一情報源で、
   * 外側 UI（PreviewModal のスライダー等）はこれを購読して位置を揃える。
   */
  onVolumeChange?: (volume: number) => void
  /**
   * dev 専用: 実行中ずっと前進入力（KeyW 相当の仮想キー）を注入し続け、
   * 視点を follow に固定してアバターを走らせ続ける。走行モーション調整画面
   * （落下しない flatField シーン）でキー操作なしに走りを観察するためのもの。既定 false。
   */
  autoForward?: boolean
  /**
   * dev 専用: 走行モーション調整パネル（RunMotionTuner）を表示するか。グローバルな
   * runMotionParams を直接 mutate するため motion-tuner エントリだけで有効化し、
   * 通常の dev プレビュー（preview-demo 等）には出さない。既定 false。
   */
  showRunMotionTuner?: boolean
}

/** 親（PreviewModal など）からパネルの再生制御を呼び出すための命令的ハンドル。 */
export interface PreviewPanelHandle {
  start: () => void
  stop: () => void
  cycleCamera: () => void
  downloadRbxlx: () => void
  /** 音声ミュートの ON/OFF を切り替える（音量 0 と直前音量をトグルする）。 */
  toggleMute: () => void
  /** 音量(0..1)を設定する（PreviewModal のスライダーから呼ぶ）。 */
  setVolume: (volume: number) => void
  /** ビューポートへフォーカスを戻す（PIP 窓間移動後のキー入力復帰に使う）。 */
  focusViewport: () => void
}

/**
 * UI 上の視点ボタンは「アバター視点（後方追従）」と「固定（旧 free、シーン全体を
 * 自由に見回す）」の 2 モードを切り替える。レンダラ側の KeyV ホットキーでは
 * 'first'（一人称）にもアクセス可能。視点切替の UI ボタンは廃止し、V キーで操作する。
 */
const nextCameraMode = (mode: CameraMode): CameraMode =>
  mode === 'follow' ? 'free' : 'follow'

/**
 * dev 専用の自動走行（autoForward）時の follow カメラ方位オフセット（ラジアン）。
 * 進行方向の真後ろ（0）から斜め後方の側面（約 120°）へ回り込み、走るアバターを
 * 横〜斜め後ろから観察できるようにする。進行方向自体は変えない。
 */
const AUTO_FORWARD_VIEW_YAW_OFFSET = (2 * Math.PI) / 3

/**
 * ブラウザ内「おためしプレイ」パネル。
 * World・three.js レンダラ・実行ランタイムを束ね、再生/停止/リセットを提供する。
 */
export const PreviewPanel = forwardRef<PreviewPanelHandle, PreviewPanelProps>(
  function PreviewPanel(
    {
      scene,
      workspaces,
      autoRun = false,
      showControls = true,
      cameraSwitchEnabled = false,
      onReady,
      onCameraModeChange,
      onRunningChange,
      onVolumeChange,
      autoForward = false,
      showRunMotionTuner = false,
    },
    ref,
  ) {
    const containerRef = useRef<HTMLDivElement>(null)
    // ビューポートを含むパネルのルート。iPad の BGM リトライ操作を capture で
    // 拾い、子要素（KeyGuide 等）の stopPropagation に影響されないようにする。
    const rootRef = useRef<HTMLDivElement>(null)
    const runtimeRef = useRef<PreviewRuntime | null>(null)
    const avatarRef = useRef<AvatarController | null>(null)
    const worldRef = useRef<EditablePreviewWorld | null>(null)
    const rendererRef = useRef<PreviewRendererHandle | null>(null)
    // 実行中フラグ。実行ボタンの連続押下を防ぐため、start で true、stop/reset で
    // false にする（初期化は停止を内包するため false に戻してよい）。
    const [running, setRunning] = useState(false)
    // 音量(0..1)。BGM・効果音（HTMLAudio）と、ジングル・大砲音等（WebAudio 合成、
    // audioMute モジュール経由）の音量をまとめて制御する。ref は HTMLAudio 生成
    // 時・再生時に最新値を参照するため（state だけだと生成コールバックが古い値を
    // 見る）。lastVolumeRef はミュート（音量 0）解除時に戻す直前の音量。
    const [volume, setVolume] = useState(1)
    const volumeRef = useRef(1)
    const lastVolumeRef = useRef(1)
    // looks_showMessage（print）の出力ログ。Roblox Studio の Output 相当として
    // ビューポート右上（ミッション GUI の帯より下）へ表示する。再生のたびに
    // クリアする。
    const [outputLines, setOutputLines] = useState<string[]>([])
    // 乗降プロンプト（'enter'=近くの車に乗れる / 'exit'=乗車中）。物理側の
    // onVehiclePrompt 通知で更新し、ビューポート下部の乗降ボタン表示に使う。
    const [vehiclePrompt, setVehiclePrompt] = useState<VehiclePromptKind>(null)
    // ミッション GUI（ScreenGui/TextLabel 相当）の HUD テキスト。タイマー等。
    // 環境ビヘイビア → runtime の onHudText 経由で更新され、null で非表示。
    // style は実機 StarterGui の TextLabel デザイン（背景・文字・枠色）の再現。
    // HUD はスロット名 → 表示内容の写像。複数スロットを同時表示できる
    // （実機の複数 ScreenGui 相当。例: RJF11 のカウントと終了表示の併記）。
    const [huds, setHuds] = useState<HudSlots>({})
    // ミッション GUI（HudOverlay）の実測配置。出力ログが帯（折り返しで
    // 高さが変わる）と重ならない top を計算するために使う。
    const [hudBounds, setHudBounds] = useState<HudBounds | null>(null)
    // 効果音（sound_play）。objectId → 音源。scene の descriptor.sound から
    // 構築し、再生は runtime の onPlaySound 通知で行う。
    const audioMapRef = useRef<Map<string, HTMLAudioElement>>(new Map())
    useEffect(() => {
      const audioMap = new Map<string, HTMLAudioElement>()
      for (const obj of scene.objects) {
        if (obj.sound) {
          const audio = new Audio(obj.sound.url)
          audio.preload = 'auto'
          audioMap.set(obj.id, audio)
        }
      }
      audioMapRef.current = audioMap
      return () => {
        for (const audio of audioMap.values()) {
          audio.pause()
          // Web Audio グラフから切り離す（旧シーンのノード/要素の蓄積を防ぐ）。
          releaseHtmlAudio(audio)
        }
        audioMapRef.current = new Map()
      }
    }, [scene])
    // BGM（rbxl で Looped かつ Playing の Sound）。プレイ開始でループ再生し、
    // 停止・シーン差し替えで止める。
    const bgmRef = useRef<HTMLAudioElement | null>(null)
    useEffect(() => {
      if (!scene.bgm) {
        bgmRef.current = null
        return
      }
      const audio = new Audio(scene.bgm.url)
      audio.preload = 'auto'
      audio.loop = true
      // Roblox Sound.Volume は 0〜10、HTMLAudio の volume は 0〜1。BGM の実値は
      // 0.05〜0.1 なのでそのまま使い、範囲外だけクランプしたうえでマスター音量を掛ける。
      // iOS で .volume が無視されるため Web Audio 経路（GainNode）で音量を効かせる。
      setHtmlAudioVolume(
        audio,
        Math.min(1, Math.max(0, scene.bgm.volume)) * volumeRef.current,
      )
      bgmRef.current = audio
      return () => {
        audio.pause()
        releaseHtmlAudio(audio)
        bgmRef.current = null
      }
    }, [scene])
    // ビヘイビア駆動の BGM 差し替え（runtime の onBgm 経由。例: RJF01 のボス戦・
    // クリア BGM）。再生中はシーン既定（bgmRef）より優先し、再実行で破棄して
    // シーン既定へ戻す。
    const bgmOverrideRef = useRef<HTMLAudioElement | null>(null)
    // 再生に失敗（iPad/iOS の WebKit が自動再生を拒否）した差し替え BGM。
    // 次のユーザー操作で鳴らし直すために保留する。
    const pendingBgmRef = useRef<HTMLAudioElement | null>(null)
    const stopBgmOverride = useCallback((): void => {
      const override = bgmOverrideRef.current
      const pending = pendingBgmRef.current
      override?.pause()
      bgmOverrideRef.current = null
      pendingBgmRef.current = null
      // Web Audio グラフから切り離す（差し替え/停止で捨てる override のノードを残さない）。
      if (override) {
        releaseHtmlAudio(override)
      }
      if (pending && pending !== override) {
        releaseHtmlAudio(pending)
      }
    }, [])
    // 保留中（自動再生を拒否された）BGM を、ユーザー操作のコールスタックで
    // 鳴らし直す。ビューポートの DOM 操作だけでなく、PreviewModal フッターの
    // 音量/ミュート操作（PreviewPanel ルート外）からも呼び、iPad での無音を防ぐ。
    const retryPendingBgm = useCallback((): void => {
      // ユーザー操作のコールスタックで共有 AudioContext を resume する。iOS は
      // ジェスチャ起点でないと suspended のままで、HTMLAudio の Web Audio 経路
      // （音量制御）・合成音が無音になるため、保留 BGM の有無に関わらず解除する。
      resumeSharedAudioContext()
      const audio = pendingBgmRef.current
      if (!audio) {
        return
      }
      audio
        .play()
        .then(() => {
          // 待機中に別 BGM へ切り替わっていたら新しい保留を消さない。
          if (pendingBgmRef.current === audio) {
            pendingBgmRef.current = null
          }
        })
        .catch(() => {
          // まだ拒否される場合は次の操作で再試行する（保留は維持）。
        })
    }, [])
    // 再生中の効果音をすべて止める（停止・再実行時の鳴り残り防止）。
    const stopAllSounds = useCallback((): void => {
      for (const audio of audioMapRef.current.values()) {
        audio.pause()
        audio.currentTime = 0
      }
    }, [])
    // 実行停止に伴う音の一括停止（効果音 + シーン既定 BGM + 差し替え BGM）。
    // 明示的な停止ボタンと、workspaces 差し替えで停止へ倒す経路の両方で使い、
    // 「停止中なのに BGM が鳴り続ける」状態を防ぐ。
    const stopPlaybackAudio = useCallback((): void => {
      stopAllSounds()
      bgmRef.current?.pause()
      stopBgmOverride()
    }, [stopAllSounds, stopBgmOverride])
    // 音量の設定。真実源は volumeRef（生成・再生コールバックが参照する同期値）。
    // HTMLAudio（BGM）は「素材音量 × マスター音量」を即時更新し、WebAudio
    // （効果音・ジングル等）は audioMute モジュール経由で次の発音から反映する
    // （鳴っている最中の効果音は短いため自然終了まで据え置く）。
    const handleSetVolume = useCallback(
      (next: number): void => {
        const clamped = Math.min(1, Math.max(0, next))
        volumeRef.current = clamped
        setVolume(clamped)
        setAudioVolume(clamped)
        const bgmBase =
          scene.bgm ? Math.min(1, Math.max(0, scene.bgm.volume)) : 0.5
        if (bgmRef.current) {
          setHtmlAudioVolume(bgmRef.current, bgmBase * clamped)
        }
        if (bgmOverrideRef.current) {
          setHtmlAudioVolume(bgmOverrideRef.current, bgmBase * clamped)
        }
        onVolumeChangeRef.current?.(clamped)
        // PreviewModal フッターの音量操作（PreviewPanel ルート外）もユーザー操作
        // なので、保留中の BGM をこのコールスタックで鳴らし直す（iPad のアンロック）。
        retryPendingBgm()
      },
      [scene, retryPendingBgm],
    )
    // ミュートアイコン: 音量 0 と直前音量をトグルする。0 にする前の音量を
    // lastVolumeRef へ退避し、解除時に戻す（退避値が 0 のときは最大へ）。
    const handleToggleMute = useCallback((): void => {
      if (volumeRef.current > 0) {
        lastVolumeRef.current = volumeRef.current
        handleSetVolume(0)
      } else {
        handleSetVolume(lastVolumeRef.current > 0 ? lastVolumeRef.current : 1)
      }
    }, [handleSetVolume])
    // アバター物理の初期化（非同期）完了時に最新の実行状態で一時停止を決める
    // ための ref。アバター操作は「実行中のみ」可能で、初期表示・停止後は凍結する。
    const runningRef = useRef(false)
    // KeyV ホットキー・親への視点ラベル通知のため、最新モードを ref に同期保持する。
    // 視点切替の UI ボタンは廃止したが、handleCycleCamera が次モード算出に参照する。
    const cameraModeRef = useRef<CameraMode>('follow')

    // scene 初期化 effect（依存 [scene]）から最新コールバックを呼ぶための ref。
    // props が差し替わっても古い closure を呼び続けないようにする。
    const onReadyRef = useRef(onReady)
    const onCameraModeChangeRef = useRef(onCameraModeChange)
    const onRunningChangeRef = useRef(onRunningChange)
    const onVolumeChangeRef = useRef(onVolumeChange)
    useEffect(() => {
      onReadyRef.current = onReady
      onCameraModeChangeRef.current = onCameraModeChange
      onRunningChangeRef.current = onRunningChange
      onVolumeChangeRef.current = onVolumeChange
    }, [onReady, onCameraModeChange, onRunningChange, onVolumeChange])

    // マウント時、WebAudio のグローバル音量をこのパネルの初期状態（最大）へ
    // 揃える。前パネル（refreshKey 再マウント前）が残した音量を新パネルが
    // 引きずらないようにする。
    useEffect(() => {
      setAudioVolume(volumeRef.current)
    }, [])

    // iPad/iOS (WebKit) はユーザー操作外の音声再生を拒否するため、ビヘイビアに
    // よる BGM の自動切替（ボス戦・ボーナス進入など）が無音になることがある。
    // 再生に失敗した BGM を保留し、次のユーザー操作（タップ/キー）で鳴らし直す。
    useEffect(() => {
      const root = rootRef.current
      if (!root) {
        return
      }
      // capture で拾い、KeyGuide 等が stopPropagation する仮想キー・乗降ボタン・
      // スライダー操作でもリトライが届くようにする。
      root.addEventListener('pointerdown', retryPendingBgm, { capture: true })
      root.addEventListener('keydown', retryPendingBgm, { capture: true })
      return () => {
        root.removeEventListener('pointerdown', retryPendingBgm, {
          capture: true,
        })
        root.removeEventListener('keydown', retryPendingBgm, { capture: true })
      }
    }, [retryPendingBgm])

    // 実行中フラグの唯一の更新経路。ref・state・外側 UI への通知を一括で行い、
    // 三者（ref / state / 親の表示）がずれないようにする。
    // 依存はすべて安定（ref と setState）なので effect の依存に入れても再実行されない。
    const updateRunning = useCallback((next: boolean): void => {
      if (runningRef.current === next) {
        return
      }
      runningRef.current = next
      setRunning(next)
      onRunningChangeRef.current?.(next)
    }, [])

    // autoForward は dev 専用。prod では import.meta.env.DEV が false 定数になり、
    // 以降の自動走行コードパスごと無効化（tree-shake）される。
    const autoForwardEnabled = import.meta.env.DEV && autoForward

    // 自動走行（autoForward）の再アサート。実行（unpause）かつアバターの reset/spawn
    // 復帰の「後」に呼ぶ前提。setPaused(false) は仮想キーを消さないが reset() は
    // virtualHeld を clear するため、unpause と reset の両方が済んだ最終状態で前進キーを
    // 入れ直すことで、どの経路でも 1 回は前進入力が通る。視点は follow + 側面オフセットに
    // 固定し、走るアバターを真横〜斜め後方から映す。
    const assertAutoForward = useCallback((): void => {
      if (!autoForwardEnabled) {
        return
      }
      cameraModeRef.current = 'follow'
      rendererRef.current?.setCameraMode('follow')
      rendererRef.current?.setFollowViewYawOffset(AUTO_FORWARD_VIEW_YAW_OFFSET)
      avatarRef.current?.setVirtualKey('KeyW', true)
      // autoForwardEnabled はマウント時定数（dev エントリの prop）。
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // 自動走行は死亡/リスポーン等で virtualHeld が clear されても止まらないよう、
    // 実行中は前進キーを定期的に入れ直して走り続けさせる（dev 専用・冪等）。
    useEffect(() => {
      if (!autoForwardEnabled || !running) {
        return
      }
      const id = window.setInterval(() => {
        avatarRef.current?.setVirtualKey('KeyW', true)
      }, 250)
      return () => window.clearInterval(id)
    }, [autoForwardEnabled, running])

    // フィールド確定起因の workspaces 差し替えを再実行として扱う期限（performance.now 基準）。
    // 実行ボタンで編集中フィールドを確定したとき、確定値が非同期（デバウンス経由）で
    // workspaces に反映されるまでの間だけ有効にする。
    // 最初の差し替えで必ず消費（クリア）し、停止・シーン差し替えでもクリアする。
    // 残すと、確定後 1 秒以内の通常編集による差し替えまで再実行と誤判定する。
    const pendingRestartUntilRef = useRef(0)
    const clearPendingRestart = useCallback((): void => {
      pendingRestartUntilRef.current = 0
    }, [])

    // 実行開始の本体。停止凍結の解除 → World・アバターの初期化 → onPlay 発火。
    // 実行ボタン（handleStart）と、フィールド確定起因の workspaces 差し替え時の
    // 再実行（workspaces effect）で共用する。依存はすべて安定参照。
    const startRun = useCallback((): void => {
      // 出力ログ・HUD は再生のたびに前回分をクリアする（Lua の再実行と同じ感覚）。
      setOutputLines([])
      setHuds({})
      // 前回再生の効果音が鳴り残らないよう頭出しで止める。
      stopAllSounds()
      // 前回プレイのビヘイビア BGM（ボス戦・クリア等）を破棄し、シーン既定へ戻す。
      stopBgmOverride()
      // BGM を頭から再生する（rbxl: Workspace の Playing な Looped Sound）。
      // 自動再生制限などで失敗してもゲーム進行には影響させない（警告のみ）。
      // BGM は Web Audio 経路（GainNode）で鳴るため、suspended な AudioContext
      // だと play() 成功でも無音になる。再生開始と同じ実行ボタンのジェスチャ内で
      // resume を必ず併発させ、iOS でシーン既定 BGM が初回無音になるのを防ぐ。
      resumeSharedAudioContext()
      const bgm = bgmRef.current
      if (bgm) {
        bgm.currentTime = 0
        bgm.play().catch((error: unknown) => {
          console.warn('[preview] BGM の再生に失敗しました', error)
        })
      }
      avatarRef.current?.setPaused(false)
      runtimeRef.current?.reset()
      avatarRef.current?.reset()
      // アバターを spawn へ戻すので、走り/ダッシュのアニメ内部状態も初期化して
      // 残留した前傾・増幅やテレポート由来の距離スパイクを防ぐ。
      rendererRef.current?.resetAvatarMotion()
      runtimeRef.current?.start()
      // 「スタートしたとき」の同期ブロックがスタート地点を回した場合に備え、
      // スポーン向きを回転後の向きへ即時整合させる（次の物理ステップの carry
      // 追従とカメラ再スナップを待つと、回転前の向きが数フレーム見える）。
      avatarRef.current?.syncSpawnFacing()
      // 俯瞰を解除して通常のアバター視点へ。アバターは spawn へ戻るため、
      // 古い停止地点から lerp で追いかけず背後へ即時スナップする。
      rendererRef.current?.setCameraOverview(false, { snapToAvatar: true })
      updateRunning(true)
      // reset() が virtualHeld を clear した後なので、ここで前進キーを入れ直す。
      assertAutoForward()
    }, [stopAllSounds, stopBgmOverride, updateRunning, assertAutoForward])

    // World / renderer / avatar は scene にのみ依存する重い初期化。
    // Blockly 編集や workspaces 差し替えでは再構築せず、アバター位置や物理状態を保つ。
    useEffect(() => {
      const container = containerRef.current
      if (!container) {
        return
      }

      // シーン差し替えは新しいパネルの初期表示。前シーンの実行状態を持ち越すと
      // 非同期で完成する新 AvatarController が凍結されないため、ここで停止へ戻す
      // （autoRun のときは runtime effect 側が改めて実行状態にする）。
      clearPendingRestart()
      updateRunning(false)

      const world = new SceneWorld(scene)
      worldRef.current = world
      const renderer = createRenderer(container, scene, world, {
        onObjectClick: (objectId) => runtimeRef.current?.emitClick(objectId),
        // KeyV はビューポート要素自身で購読する。WASD（physicsController）と
        // 同じく「ビューポートにフォーカスがあるとき効く」挙動に揃い、PIP で
        // ホスト DOM ごと別窓へ移動してもリスナが要素に追従する（window 購読
        // だと opener 側に残って PIP 窓で効かなくなる）。
        keyboardTarget: container,
        // 視点切替（KeyV）は devtools 限定（製品仕様はアバター視点のみ）。
        cameraHotkeyEnabled: cameraSwitchEnabled,
        // V キー・命令ハンドル経由のどちらの視点変更も一元的に受け、
        // ref（同期）・親（フッターのラベル）へ反映する。
        onCameraModeChange: (mode) => {
          cameraModeRef.current = mode
          onCameraModeChangeRef.current?.(mode)
        },
      })
      rendererRef.current = renderer
      // 既定はアバター視点。レンダラ側の cameraMode 初期値 ('free') と齟齬を出さない。
      // この呼び出しで onCameraModeChange も発火し、初期ラベルも同期される。
      // 現在モードの真実源は ref（KeyV の同期更新を取りこぼさない）。scene 再生成と
      // 視点変更が近接しても、state closure の stale な値で初期化しない。
      renderer.setCameraMode(cameraModeRef.current)
      // follow（アバター視点）の初期表示は停止中なので俯瞰で始める。free/first は
      // ユーザー操作中のカメラを奪わないため対象外（俯瞰は follow 専用の上書き）。
      // autoRun の場合は runtime effect 側が実行開始と同時に俯瞰を解除する。
      renderer.setCameraOverview(true)

      // アバター物理は WASM 初期化があるため非同期。マウント解除済みなら破棄する。
      // キーボードはビューポート要素にスコープする（window 占有を避ける）。
      let cancelled = false
      createAvatarController(
        scene,
        world,
        (objectId) => runtimeRef.current?.emitTouch(objectId),
        container,
        // follow（アバター視点）時のカメラ方位を渡し、視点方向へ進む移動にする。
        () => rendererRef.current?.getMovementBasisYaw() ?? null,
        // 乗降プロンプトの表示状態（乗降ボタン）を React 側へ反映する。
        (prompt) => setVehiclePrompt(prompt),
      )
        .then((controller) => {
          if (cancelled) {
            controller.dispose()
            return
          }
          avatarRef.current = controller
          // アバター操作は実行中のみ。初期表示（未実行）は停止後と同じ凍結状態に
          // しておき、実行ボタン（handleStart）で解除する。
          controller.setPaused(!runningRef.current)
          // dev 専用の自動走行: autoRun で running=true が先に確定し、その後この
          // 非同期生成が解決するケースを取りこぼさないよう、unpause 済み
          // （running=true）ならここでも前進キーを入れ直す。未実行ならここでは
          // 入れず、後続の handleStart/autoRun effect の assertAutoForward に委ねる
          // （paused 中の setVirtualKey は no-op のため）。
          if (runningRef.current) {
            assertAutoForward()
          }
          // ジャンプ屈伸など物理由来の姿勢アニメをレンダラに繋ぐ。
          // nowMs はレンダラの rAF タイムスタンプ。描画補間位置（renderPosition）
          // を物理ループと同じ vsync 基準の時計で評価するために引き渡す。
          rendererRef.current?.setAvatarMotionStateProvider((nowMs) =>
            controller.getMotionState(nowMs),
          )
          // 物理が整い操作可能になった合図。読み込みオーバーレイを閉じる。
          onReadyRef.current?.()
        })
        .catch((error) => {
          console.error('アバター物理の初期化に失敗しました', error)
          // 物理初期化に失敗してもシーン自体は描画済み。
          // 読み込み表示で固まらないよう、未マウント時を除き ready を通知する。
          if (!cancelled) {
            onReadyRef.current?.()
          }
        })

      return () => {
        cancelled = true
        // motion provider はレンダラ破棄前にクリアして dangling 参照を防ぐ。
        rendererRef.current?.setAvatarMotionStateProvider(null)
        avatarRef.current?.dispose()
        avatarRef.current = null
        // 破棄済みコントローラのプロンプトを画面に残さない。
        setVehiclePrompt(null)
        renderer.dispose()
        rendererRef.current = null
        worldRef.current = null
      }
      // 視点モードは cameraModeRef（ref）から読むため依存に現れない。重い初期化は
      // scene 変更時のみ行い、視点切替はボタン/KeyV から setCameraMode を直接呼ぶ。
      // cameraSwitchEnabled はレンダラ生成時の固定オプション（実質マウント時定数）の
      // ため、変更時はレンダラごと作り直す。updateRunning / clearPendingRestart は
      // 安定参照（useCallback []）。autoForward は dev エントリのマウント時定数で、
      // 変化時に重い初期化を再実行させないため意図的に依存へ含めない。
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scene, cameraSwitchEnabled, updateRunning, clearPendingRestart])

    // workspaces は実行系（runtime）のみ差し替える。World / renderer / avatar は触らない。
    // 動画チャプター切替や Blockly 編集で workspaces だけが更新された場合、World や
    // アバター位置はそのまま保持される。autoRun はマウント時のみ参照する初期挙動なので、
    // 依存に含めずに新規 runtime には反映しない（明示的に start ボタンで開始してもらう）。
    useEffect(() => {
      const world = worldRef.current
      if (!world) {
        return
      }
      // テンプレート rbxl のギミック（ミッション別 TS 実装）または
      // タグ由来の汎用ギミック（外部 rbxl）を解決して runtime に渡す。
      const behaviorsFactory = behaviorsForScene(scene)
      const runtime = createRuntime(
        scene,
        world,
        workspaces,
        behaviorsFactory,
        {
          // 「～と出力に表示する」（print）を出力ログへ流す。直近 200 行を
          // 保持する（スクロールで過去の出力を遡れる）。
          onOutput: (text) =>
            setOutputLines((previous) => [...previous.slice(-199), text]),
          // ミッション GUI（タイマー等）の HUD テキスト。
          onHudText: (text, style, slot) =>
            setHuds((prev) => {
              if (text === null) {
                // slot 省略の null は全スロット消去（停止/非表示）。
                if (slot === undefined) {
                  return {}
                }
                if (!(slot in prev)) {
                  return prev
                }
                const next = { ...prev }
                delete next[slot]
                return next
              }
              return { ...prev, [slot ?? 'default']: { text, style } }
            }),
          // 「音を鳴らす」（Sound:Play()）。World の現在音量（Sound.Volume、
          // 0〜10）を HTMLAudio の範囲 0〜1 にクランプして頭から再生する。
          onPlaySound: (objectId) => {
            const audio = audioMapRef.current.get(objectId)
            if (!audio) {
              return
            }
            const soundVolume =
              worldRef.current?.getState().get(objectId)?.soundVolume ?? 1
            setHtmlAudioVolume(
              audio,
              Math.min(1, Math.max(0, soundVolume)) * volumeRef.current,
            )
            audio.currentTime = 0
            audio.play().catch((error: unknown) => {
              // 自動再生制限（ユーザー操作前の再生拒否）等。実行は止めない。
              console.warn('[preview] 効果音の再生に失敗しました', error)
            })
          },
          // ビヘイビアによる BGM 差し替え（例: RJF01 のボス戦・クリア BGM）。
          // 再生中の BGM（シーン既定 or 前回の差し替え）を止め、新しい音源を
          // ループ再生する。音量はシーン既定 BGM に揃える（全曲同じ聞こえ方）。
          onBgm: (url) => {
            bgmRef.current?.pause()
            stopBgmOverride()
            const audio = new Audio(url)
            audio.loop = true
            setHtmlAudioVolume(
              audio,
              Math.min(1, Math.max(0, scene.bgm?.volume ?? 0.5)) *
                volumeRef.current,
            )
            bgmOverrideRef.current = audio
            audio.play().catch((error: unknown) => {
              // 非同期 reject までに停止・再実行・別 BGM へ切り替わっていたら
              // （最新の override でなければ）保留しない。
              if (bgmOverrideRef.current !== audio) {
                return
              }
              // iPad/iOS (WebKit) はユーザー操作外の再生を拒否する。次の
              // ユーザー操作（タップ/キー）で鳴らし直すため保留する。
              console.warn('[preview] BGM の切り替えに失敗しました', error)
              pendingBgmRef.current = audio
            })
          },
        },
      )
      runtimeRef.current = runtime
      // フィールド確定起因の再実行予約は、由来を問わず最初の差し替えで消費する
      // （残すと猶予内の通常編集による差し替えまで再実行と誤判定する）。
      const hasPendingRestart =
        performance.now() < pendingRestartUntilRef.current
      clearPendingRestart()
      if (autoRun) {
        runtime.start()
        // autoRun は実行開始扱い。アバター操作も解放する（実行中のみ操作可能）。
        updateRunning(true)
        avatarRef.current?.setPaused(false)
        // 実行中＝通常のアバター視点（scene effect が設定した初期俯瞰を解除）。
        rendererRef.current?.setCameraOverview(false)
        // dev 専用の自動走行: controller が既に ready ならここで前進キーを入れる
        // （未 ready なら controller 生成 then 側が running=true を見て入れる）。
        assertAutoForward()
      } else if (runningRef.current) {
        if (hasPendingRestart) {
          // 実行ボタンでフィールドを確定した直後の差し替え。確定値を反映した
          // 新しいプログラムで実行し直す（1 クリックで「確定 → 実行」を完結
          // させる。停止に倒すと確定のたびに実行ボタンの 2 度押しが要る）。
          startRun()
        } else {
          // 実行中に workspaces だけ差し替わった場合、旧 runtime は dispose 済みで
          // 新 runtime は start されるまで止まっている。実行状態も停止へ同期し、
          // 「停止中なのに停止ボタンが押せる」ねじれを防ぐ。音も実行状態と
          // 揃えて止める（BGM だけ鳴り続けないようにする）。
          stopPlaybackAudio()
          updateRunning(false)
          avatarRef.current?.setPaused(true)
          rendererRef.current?.setCameraOverview(true)
        }
      }
      return () => {
        runtime.dispose()
        runtimeRef.current = null
        // 差し替え BGM はこの runtime のプレイに紐づく。鳴り残りを防ぐため
        // runtime の破棄と同時に止める（シーン既定 BGM は scene effect が管理）。
        stopBgmOverride()
      }
      // autoRun は意図的に依存に入れない（mount 時のみ評価）。
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      scene,
      workspaces,
      updateRunning,
      startRun,
      clearPendingRestart,
      stopBgmOverride,
      stopPlaybackAudio,
      assertAutoForward,
    ])

    // ボタン押下後はビューポートへフォーカスを戻し、矢印キー/WASD が即効くようにする。
    const focusViewport = (): void => {
      containerRef.current?.focus()
    }

    // 再生 = 最初からプレイ。停止で凍結した状態を解除し、World・アバターを
    // 初期状態へ戻してから onPlay を発火する（前回実行の結果が上乗せされない）。
    const handleStart = (): void => {
      // 編集途中の Blockly フィールド値（数値入力等）を確定してから実行する。
      // 確定が発生した場合、確定値は非同期（change イベント → デバウンス）で
      // workspaces に届くため、差し替え時の再実行を猶予つきで予約しておく
      // （予約がないと差し替えで停止に倒れ、実行ボタンの 2 度押しが要る）。
      if (commitBlocklyFieldEdits()) {
        pendingRestartUntilRef.current =
          performance.now() + FIELD_COMMIT_RESTART_GRACE_MS
      }
      startRun()
      focusViewport()
    }

    // 停止 = プログラム実行を中断し、物理とキャラクター操作も止める。
    // アバターはリスポーン地点へ戻す（ワールドの状態はそのまま。全体を
    // 初期状態へ戻すのは次の再生ボタン押下時）。
    const handleStop = (): void => {
      // 停止済みなら何もしない（ボタン非活性に加えて、命令ハンドル経由の
      // 重複呼び出しに対する多重防御）。
      if (!runningRef.current) {
        return
      }
      // 明示的な停止は再実行予約より優先する（予約が残ると、停止後の差し替えを
      // 再実行と誤判定し得る）。
      clearPendingRestart()
      runtimeRef.current?.stop()
      // 実行停止と同時に鳴っている効果音・BGM（差し替え分も含む）も止める。
      stopPlaybackAudio()
      avatarRef.current?.setPaused(true)
      // アバターをリスポーン地点へ戻す（どこで停止しても再開位置が安定する）。
      avatarRef.current?.reset()
      rendererRef.current?.resetAvatarMotion()
      // 本家 Roblox の停止時に合わせ、カメラを上から見下ろす遠目の俯瞰へ引く。
      rendererRef.current?.setCameraOverview(true)
      updateRunning(false)
      focusViewport()
    }

    const handleCycleCamera = (): void => {
      // renderer.setCameraMode 内の onCameraModeChange が ref・React state・親への
      // 通知を一括で行うため、ここでは次モードの算出と適用のみ行う。
      // 最新モードは ref から読む（KeyV 直後でも stale にならない）。
      const mode = nextCameraMode(cameraModeRef.current)
      rendererRef.current?.setCameraMode(mode)
      focusViewport()
    }

    // 親（PreviewModal）がヘッダーのボタンからパネルを操作するための命令的ハンドル。
    // 依存配列を省略し毎レンダで最新ハンドラを束ねる（cameraMode 等の stale 参照を防ぐ）。
    useImperativeHandle(ref, () => ({
      start: handleStart,
      stop: handleStop,
      cycleCamera: handleCycleCamera,
      downloadRbxlx: handleDownloadRbxlx,
      toggleMute: handleToggleMute,
      setVolume: handleSetVolume,
      focusViewport,
    }))

    const handleDownloadRbxlx = (): void => {
      const world = worldRef.current
      if (!world) {
        return
      }
      const filename = `${scene.missionId || 'creation'}.rbxlx`
      try {
        downloadRbxlx(world, filename)
      } catch (error) {
        console.error('rbxlx の書き出しに失敗しました', error)
      }
    }

    return (
      <div
        ref={rootRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          // プレビューは「操作する画面」であり文章ではない。HUD やボタンの文字を
          // 長押し/ドラッグで選択させない（user-select は継承するので配下の
          // GUI 全体に効く）。iPad の長押し選択メニューも touch-callout で抑止する。
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
        }}
      >
        <div
          style={{
            // ビューポートを基準にオーバーレイ（キーガイド）を絶対配置する。
            position: 'relative',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            ref={containerRef}
            tabIndex={0}
            // Blockly のフィールド編集中（数値入力等）にプレビューをクリック
            // しても、エディタ側の input が開いたままだと値が確定されない。
            // ビューポートへの操作開始で明示的に閉じて確定させる。
            onPointerDown={commitBlocklyFieldEdits}
            style={{
              width: '100%',
              height: '100%',
              outline: 'none',
            }}
          />
          {/* アバターを操作できるシーンでのみ、右下に操作キーガイドを常時表示する。
              タップ/クリックでも対応キーの操作ができる（タブレット向け）。 */}
          {scene.avatar && (
            <KeyGuide
              onVirtualKey={(code, pressed) =>
                avatarRef.current?.setVirtualKey(code, pressed)
              }
              // 乗車中はキーの役割が運転（前進/バック/ブレーキ）に変わる。
              mode={vehiclePrompt === 'exit' ? 'drive' : 'walk'}
            />
          )}
          {scene.avatar && running && (
            <VehiclePromptButton
              vehiclePrompt={vehiclePrompt}
              onTrigger={() => {
                avatarRef.current?.triggerVehicleAction()
                // ボタンがフォーカスを奪うため、乗降後にビューポートへ戻して
                // WASD/矢印キーが再クリックなしで効くようにする（E キー操作と同じ状態へ）。
                focusViewport()
              }}
            />
          )}
          <OutputLog
            lines={outputLines}
            // 出力ログは上端の wide 帯のぶんだけ下へ逃がす。複数スロット時は
            // wide 帯（あれば）の存在判定に使う（実際の縦位置は hudBounds 実測）。
            hudStyle={
              Object.values(huds).find((entry) => entry.style?.wide)?.style
            }
            hudBounds={hudBounds}
          />
          <HudOverlay huds={huds} onBoundsChange={setHudBounds} />
          {/* 走行モーション調整パネルは dev サーバ起動時かつ motion-tuner エントリ
              （showRunMotionTuner）でのみ表示する開発者ツール。出荷ビルドには含めない。 */}
          {import.meta.env.DEV && showRunMotionTuner && scene.avatar && (
            <RunMotionTuner />
          )}
        </div>
        {showControls && (
          <PreviewControlsBar
            running={running}
            volume={volume}
            onStart={handleStart}
            onStop={handleStop}
            onVolumeChange={handleSetVolume}
            onToggleMute={handleToggleMute}
          />
        )}
      </div>
    )
  },
)
